import api from './api';
import { Usuario } from '../types';

export interface UsuarioResponse {
  success: boolean;
  message?: string;
  usuarios?: Usuario[];
  usuario?: Usuario;
  senha_temporaria?: string;
  liderancas?: any[];
}

export interface NovoUsuarioData {
  nome: string;
  email: string;
  cargo: string;
  departamento_id: string;
  tipo: 'admin' | 'comum' | 'expert';
  avatar?: string; // Avatar é opcional, será definido como padrão
}

export interface AtualizarUsuarioData {
  nome?: string;
  email?: string;
  cargo?: string;
  departamento_id?: string;
  eh_lider?: boolean;
  tipo?: 'admin' | 'comum' | 'expert';
  ativo?: boolean;
  avatar?: string; // Avatar é opcional, será definido como padrão
}

/**
 * Serviço para gerenciar usuários
 */
const usuarioService = {
  /**
   * Obter lideranças de um usuário
   * @param usuarioId ID do usuário
   * @returns Promise com a lista de departamentos onde o usuário é líder
   */
  obterLiderancas: async (usuarioId: string): Promise<UsuarioResponse> => {
    try {
      const response = await api.get<UsuarioResponse>(`/usuarios/${usuarioId}/liderancas`);
      return response.data;
    } catch (error: any) {
      console.error(`Erro ao obter lideranças do usuário ${usuarioId}:`, error);
      return {
        success: false,
        message: error.response?.data?.message || 'Erro ao obter lideranças do usuário'
      };
    }
  },

  /**
   * Obter lista simplificada de usuários para dropdown
   * @param departamentoId ID do departamento para filtrar (opcional)
   * @returns Promise com a lista de usuários
   */
  obterParaDropdown: async (departamentoId?: string): Promise<UsuarioResponse> => {
    try {
      let url = '/usuarios/dropdown';
      if (departamentoId) {
        url += `?departamento_id=${departamentoId}`;
      }
      
      const response = await api.get<UsuarioResponse>(url);
      return response.data;
    } catch (error: any) {
      console.error('Erro ao obter usuários para dropdown:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Erro ao obter usuários'
      };
    }
  },
  
  /**
   * Obter todos os usuários do sistema sem filtros
   * @returns Promise com a lista completa de usuários
   */
  obterTodos: async (): Promise<UsuarioResponse> => {
    try {
      const response = await api.get<UsuarioResponse>('/usuarios');
      return response.data;
    } catch (error: any) {
      console.error('Erro ao obter todos os usuários:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Erro ao obter usuários'
      };
    }
  },

  /**
   * Obter todos os usuários do sistema com dados completos para administração
   * @returns Promise com a lista completa de usuários para administração
   */
  obterTodosAdmin: async (): Promise<UsuarioResponse> => {
    try {
      const response = await api.get<UsuarioResponse>('/usuarios/admin');
      return response.data;
    } catch (error: any) {
      console.error('Erro ao obter usuários para administração:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Erro ao obter usuários para administração'
      };
    }
  },
  /**
   * Criar um novo usuário (apenas para administradores)
   * @param dadosUsuario Dados do novo usuário
   * @returns Promise com o resultado da operação
   */
  criarUsuario: async (dadosUsuario: NovoUsuarioData): Promise<UsuarioResponse> => {
    try {
      // Sempre define a imagem padrão, ignorando qualquer valor fornecido
      const dadosComAvatarPadrao = {
        ...dadosUsuario,
        avatar: '/profilepicture.png'
      };
      const response = await api.post<UsuarioResponse>('/usuarios', dadosComAvatarPadrao);
      return response.data;
    } catch (error: any) {
      console.error('Erro ao criar usuário:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Erro ao criar usuário'
      };
    }
  },

  /**
   * Atualizar um usuário existente (apenas para administradores)
   * @param id ID do usuário a ser atualizado
   * @param dadosUsuario Dados atualizados do usuário
   * @returns Promise com o resultado da operação
   */
  atualizarUsuario: async (id: string, dadosUsuario: AtualizarUsuarioData): Promise<UsuarioResponse> => {
    try {
      // Sempre define a imagem padrão, ignorando qualquer valor fornecido
      const dadosComAvatarPadrao = {
        ...dadosUsuario,
        avatar: '/profilepicture.png'
      };
      const response = await api.put<UsuarioResponse>(`/usuarios/${id}`, dadosComAvatarPadrao);
      return response.data;
    } catch (error: any) {
      console.error('Erro ao atualizar usuário:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Erro ao atualizar usuário'
      };
    }
  },

  /**
   * Resetar a senha de um usuário (apenas para administradores)
   * @param id ID do usuário para resetar a senha
   * @returns Promise com o resultado da operação e a senha temporária
   */
  resetarSenha: async (id: string): Promise<UsuarioResponse> => {
    try {
      const response = await api.post<UsuarioResponse>(`/usuarios/${id}/reset-senha`);
      return response.data;
    } catch (error: any) {
      console.error('Erro ao resetar senha:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Erro ao resetar senha'
      };
    }
  },

  /**
   * Excluir um usuário (apenas para administradores)
   * @param id ID do usuário a ser excluído
   * @returns Promise com o resultado da operação
   */
  excluirUsuario: async (id: string): Promise<UsuarioResponse> => {
    try {
      const response = await api.delete<UsuarioResponse>(`/usuarios/${id}`);
      return response.data;
    } catch (error: any) {
      console.error('Erro ao excluir usuário:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Erro ao excluir usuário'
      };
    }
  }
};

export default usuarioService;
